import { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { createAxiosInstance, API_URL } from "../../../api";
import { useProfileEvent } from "./useProfileEvents";

export type StepCompletionStatus = {
  isCompleted: boolean;
  isOptional: boolean;
  hasErrors?: boolean;
};

export function useStepCompletion() {
  const profileUser = useSelector((state: any) => state.profile?.user || {});
  const { status: accountStatus } = useSelector((state: any) => state.main) || {};
  const userId = useSelector((state: any) => state.main?.userId);

  const {
    avatar,
    about,
    title,
    gender,
    online,
    faceToFace,
    qualifications,
    email,
    phone,
    isSubmitted,
    _id,
  } = profileUser;

  // Check if user has subjects (fetch from API)
  const [hasSubjects, setHasSubjects] = useState(false);

  useEffect(() => {
    if (!userId) {
      setHasSubjects(false);
      return;
    }

    const checkSubjects = async () => {
      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_SERVER}/api/subjects/user/${userId}`
        );
        const subjects = response.data || [];
        setHasSubjects(subjects.length > 0);
      } catch (error: any) {
        console.error("Error checking subjects:", error);
        setHasSubjects(false);
      }
    };

    checkSubjects();
  }, [userId]);

  // Listen for subject changes using custom hook
  useProfileEvent('subjectsUpdated', () => {
    if (!userId) return;
    createAxiosInstance()
      .get(`${process.env.REACT_APP_SERVER}/api/subjects/user/${userId}`)
      .then((res) => setHasSubjects((res.data || []).length > 0))
      .catch(() => setHasSubjects(false));
  }, [userId]);

  // Check billing status by fetching from API
  const [hasPaid, setHasPaid] = useState(false);

  useEffect(() => {
    if (!userId) {
      setHasPaid(false);
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${API_URL}/api/billings/hasPaid/${userId}`
        );

        const paidStatus = response.data?.paid;
        const isPaid = paidStatus === "Paid" || paidStatus === true;
        setHasPaid(isPaid);
      } catch (error: any) {
        console.error("Error checking payment status:", error);
        setHasPaid(false);
      }
    };

    checkPaymentStatus();
  }, [userId]);

  // Listen for payment completion using custom hook
  useProfileEvent('paymentCompleted', () => {
    if (!userId) return;
    createAxiosInstance()
      .post(`${API_URL}/api/billings/hasPaid/${userId}`)
      .then((res) => {
        const paidStatus = res.data?.paid;
        setHasPaid(paidStatus === "Paid" || paidStatus === true);
      })
      .catch(() => setHasPaid(false));
  }, [userId]);

  const completions = useMemo(
    () => ({
      subjects: {
        isCompleted: hasSubjects,
        isOptional: false,
      },
      "tutoring-options": {
        isCompleted: Boolean(online || faceToFace),
        isOptional: false,
      },
      "tutor-profile": {
        isCompleted: Boolean(
          title && gender !== -1 && about && about !== "<p><br></p>" && avatar
        ),
        isOptional: false,
      },
      qualifications: {
        isCompleted: Object.keys(qualifications || {}).some(
          (k) => qualifications[k] !== -1
        ),
        isOptional: false,
      },
      "contact-info": {
        isCompleted: Boolean(email || phone),
        isOptional: true,
      },
      subscription: {
        isCompleted: hasPaid,
        isOptional: false,
      },
      submit: {
        isCompleted: Boolean(isSubmitted),
        isOptional: false,
      },
    }),
    [
      hasSubjects,
      online,
      faceToFace,
      title,
      gender,
      about,
      avatar,
      qualifications,
      email,
      phone,
      hasPaid,
      isSubmitted,
    ]
  );

  return completions;
}

